type Nullable = string | null | undefined;

type RecommendationsPodMetadata = {
  podId: Nullable;
  resultId?: Nullable;
  seedItems?: Nullable;
  numResults: number;
};

type RecommendationItemMetadata = {
  isRecommendation: boolean;
  strategyId?: Nullable;
};

export type RecommendationsPodAttributes = {
  "data-cnstrc-recommendations": true;
  "data-cnstrc-num-results": number;
  "data-cnstrc-recommendations-pod-id": string;
  "data-cnstrc-recommendations-seed-items"?: string;
  "data-cnstrc-result-id"?: string;
};

export type RecommendationItemAttributes = {
  "data-cnstrc-item": "recommendation";
  "data-cnstrc-strategy-id"?: string;
};

type ConversionItem = {
  id: string;
  name: string;
  price: number;
};

export type ConversionItemAttributes = {
  "data-cnstrc-item-id": string;
  "data-cnstrc-item-name": string;
  "data-cnstrc-item-price": number;
};

const isPresent = (value: Nullable): value is string =>
  typeof value === "string" && value.length > 0;

/** A pod id marks a tracked recommendation pod regardless of engine; result id and seed items are attached only when the backend sent them. */
export function getRecommendationsPodAttributes({
  podId,
  resultId,
  seedItems,
  numResults,
}: RecommendationsPodMetadata):
  | RecommendationsPodAttributes
  | Record<string, never> {
  if (!isPresent(podId)) {
    return {};
  }

  const attributes: RecommendationsPodAttributes = {
    "data-cnstrc-recommendations": true,
    "data-cnstrc-num-results": numResults,
    "data-cnstrc-recommendations-pod-id": podId,
  };

  if (isPresent(seedItems)) {
    attributes["data-cnstrc-recommendations-seed-items"] = seedItems;
  }

  if (isPresent(resultId)) {
    attributes["data-cnstrc-result-id"] = resultId;
  }

  return attributes;
}

/** Recommendation items carry a strategy id only when the backend sent one. */
export function getRecommendationItemAttributes({
  isRecommendation,
  strategyId,
}: RecommendationItemMetadata):
  | RecommendationItemAttributes
  | Record<string, never> {
  if (!isRecommendation) {
    return {};
  }

  const attributes: RecommendationItemAttributes = {
    "data-cnstrc-item": "recommendation",
  };

  if (isPresent(strategyId)) {
    attributes["data-cnstrc-strategy-id"] = strategyId;
  }

  return attributes;
}

/** The beacon resolves an add_to_cart click via `closest()`, so a button portaled out of the product container needs these on its own ancestor. */
export function getConversionItemAttributes({
  id,
  name,
  price,
}: ConversionItem): ConversionItemAttributes {
  return {
    "data-cnstrc-item-id": id,
    "data-cnstrc-item-name": name,
    "data-cnstrc-item-price": price,
  };
}
